// Export the tour steps for Share Resources feature
export const shareResourcesGuideSteps = [
  {
    element: "#SR-add-resource-button",
    popover: {
      title: `<div class="flex items-center gap-3 mb-3">
            <div class="w-8 h-8 bg-brand-100 rounded-full flex items-center justify-center">
              <svg class="w-4 h-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
              </svg>
            </div>
            <span class="text-base font-semibold text-foreground">Add New Resource</span>
          </div>`,
      description: `<div class="space-y-3">
          <p class="text-sm text-muted-foreground">Click this button to create a new resource to share with your affiliates. Resources help you provide valuable information, training materials, or promotional content.</p>
          <div class="bg-brand-50 p-3 rounded-lg">
            <ul class="text-xs text-brand-800 space-y-1">
              <li>• Create guides, tutorials, or promotional materials</li>
              <li>• Share best practices with your affiliates</li>
              <li>• Add resources to help affiliates succeed</li>
              <li>• All resources are visible to approved affiliates</li>
            </ul>
          </div>
        </div>`,
      side: "bottom" as const,
      align: "end" as const,
    },
  },
  {
    element: "#SR-resource-title-field",
    popover: {
      title: `<div class="flex items-center gap-3 mb-3">
            <div class="w-8 h-8 bg-brand-100 rounded-full flex items-center justify-center">
              <svg class="w-4 h-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
              </svg>
            </div>
            <span class="text-base font-semibold text-foreground">Resource Title</span>
          </div>`,
      description: `<div class="space-y-3">
          <p class="text-sm text-muted-foreground">Enter a clear and descriptive title for your resource. This helps affiliates quickly identify the content and purpose.</p>
          <div class="bg-brand-50 p-3 rounded-lg">
            <ul class="text-xs text-brand-800 space-y-1">
              <li>• Be specific and descriptive</li>
              <li>• Example: "Instagram Story Templates"</li>
              <li>• Example: "Affiliate Marketing Best Practices"</li>
              <li>• Keep it concise but informative</li>
            </ul>
          </div>
        </div>`,
      side: "right" as const,
      align: "start" as const,
    },
  },
  {
    element: "#SR-resource-content-field",
    popover: {
      title: `<div class="flex items-center gap-3 mb-3">
            <div class="w-8 h-8 bg-brand-100 rounded-full flex items-center justify-center">
              <svg class="w-4 h-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
              </svg>
            </div>
            <span class="text-base font-semibold text-foreground">Resource Content</span>
          </div>`,
      description: `<div class="space-y-3">
          <p class="text-sm text-muted-foreground">Create rich, engaging content for your affiliates. Use the editor's formatting tools to make your content easy to read and understand.</p>
          <div class="bg-brand-50 p-3 rounded-lg">
            <ul class="text-xs text-brand-800 space-y-1">
              <li>• Use headings and formatting for clarity</li>
              <li>• Add YouTube links - they auto-embed as videos</li>
              <li>• Include step-by-step instructions</li>
              <li>• Add images for visual guidance</li>
              <li>• Make it actionable and helpful</li>
            </ul>
          </div>
        </div>`,
      side: "right" as const,
      align: "start" as const,
    },
  },
  {
    element: "#SR-submit-resource-button",
    popover: {
      title: `<div class="flex items-center gap-3 mb-3">
            <div class="w-8 h-8 bg-brand-100 rounded-full flex items-center justify-center">
              <svg class="w-4 h-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
              </svg>
            </div>
            <span class="text-base font-semibold text-foreground">Save Resource</span>
          </div>`,
      description: `<div class="space-y-3">
          <p class="text-sm text-muted-foreground">Click this button to save and publish your resource. It will immediately become available to all your approved affiliates.</p>
          <div class="bg-brand-50 p-3 rounded-lg">
            <ul class="text-xs text-brand-800 space-y-1">
              <li>• Resources are published instantly</li>
              <li>• Affiliates can view them in their dashboard</li>
              <li>• You can edit or delete resources anytime</li>
              <li>• Both title and content are required</li>
            </ul>
          </div>
        </div>`,
      side: "top" as const,
      align: "end" as const,
    },
  },
  {
    element: "#SR-resources-list",
    popover: {
      title: `<div class="flex items-center gap-3 mb-3">
            <div class="w-8 h-8 bg-brand-100 rounded-full flex items-center justify-center">
              <svg class="w-4 h-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 10h16M4 14h16M4 18h16"></path>
              </svg>
            </div>
            <span class="text-base font-semibold text-foreground">Resources List</span>
          </div>`,
      description: `<div class="space-y-3">
          <p class="text-sm text-muted-foreground">View and manage all your created resources. Each resource card shows a preview and provides quick actions.</p>
          <div class="bg-brand-50 p-3 rounded-lg">
            <ul class="text-xs text-brand-800 space-y-1">
              <li>• Preview: View the full resource content</li>
              <li>• Edit: Modify title or content anytime</li>
              <li>• Delete: Remove resources you no longer need</li>
              <li>• Resources are shown newest first</li>
            </ul>
          </div>
        </div>`,
      side: "right" as const,
      align: "start" as const,
    },
  },
];
